import { HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { filter, map, startWith, takeWhile, withLatestFrom } from 'rxjs/operators';

import { Upload, UploadStatus } from '@common/entities/upload/upload.entity';
import { HttpService } from '@common/services/http/http.service';

@Injectable()
export class UploadSandboxService {
    private cancelUploadItem$: Subject<string> = new Subject<string>();

    constructor(private httpService: HttpService) {}

    upload(endpoint: string, file: File): Observable<Upload> {
        const formData: FormData = new FormData();
        formData.append('file', file);

        const upload: Upload = {
            file,
            state: {
                status: UploadStatus.UPLOADING,
                progress: 0
            }
        };
        const cancel$: Observable<Upload> = this.initCancelUploadItemStream(upload);

        return this.httpService
            .post<HttpEvent<any>>(endpoint, formData, {
                reportProgress: true,
                observe: 'events'
            })
            .pipe(
                withLatestFrom(cancel$.pipe(startWith(undefined))),
                takeWhile(([_, cancel]) => !cancel, true),
                map(
                    ([httpEvent, canceled]: [HttpEvent<any>, Upload | undefined]): Upload =>
                        this.mapEventToUpload(httpEvent, upload)
                )
            );
    }

    cancelUpload(upload: Upload): void {
        this.cancelUploadItem$.next(upload.file.name);
    }

    private mapEventToUpload(event: HttpEvent<any>, upload: Upload): Upload {
        switch (event.type) {
            case HttpEventType.UploadProgress:
                return this.handleUploadProgressType(event, upload);
            case HttpEventType.Response:
                return this.handleResponseType(event, upload);
            default:
                return upload;
        }
    }

    private handleResponseType(event: HttpResponse<any>, item: Upload): Upload {
        const update: Upload = { ...item };

        update.state.status = UploadStatus.UPLOADED;
        update.state.progress = 100;

        return update;
    }

    private handleUploadProgressType(event: HttpProgressEvent, item: Upload): Upload {
        const update: Upload = { ...item };
        const progress: number = event.total ? Math.round((75 * event.loaded) / event.total) : 0;

        if (progress === 75) {
            update.state.status = UploadStatus.PROCESSING;
        }

        update.state.progress = progress;

        return update;
    }

    private initCancelUploadItemStream(upload: Upload): Observable<Upload> {
        return this.cancelUploadItem$.pipe(
            filter(fileName => fileName === upload.file.name),
            map(id => {
                upload.state = {
                    status: UploadStatus.CANCELED,
                    progress: 100
                };

                return upload;
            })
        );
    }
}
